import { AfterViewInit, ChangeDetectionStrategy, Component } from '@angular/core';
import { decode } from '@googlemaps/polyline-codec';
import { greyMapThemeStyles } from '../../../shared/google-map/grey-map-theme-styles';
import { mapCenterDefault } from '../../../shared/google-map/models/map-center-default';

@Component({
  selector: 'wp-polyline-viewer',
  template: `
    <div class="container">
      <h1>Polyline Viewer</h1>
      <input id="input" type="text" placeholder="Enter polyline (e.g., _p~iF~ps|U_ulLnnqC_mqNvxq\`@)" />
      <div style="display: flex; margin-bottom: 20px">
        <div style="display: flex; margin-right: 20px">
          <input id="show-markers" type="checkbox" />
          <label for="show-markers">Show markers</label>
        </div>
        <div style="display: flex; margin-right: 20px">
          <input id="unescape" type="checkbox" />
          <label for="unescape">Unescape '\\'</label>
        </div>
        <div style="display: flex; margin-right: 20px">
          <input id="precision5" type="checkbox" />
          <label for="precision5">Precision 5</label>
        </div>
      </div>
      <div id="map"></div>
    </div>
    <div class="points"></div>
  `,
  styleUrl: './polyline-viewer.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PolylineViewerComponent implements AfterViewInit {
  private map: google.maps.Map;

  ngAfterViewInit(): void {
    this.init();
  }

  private async init(): Promise<void> {
    this.initInputsAndMap();
  }

  private initInputsAndMap(): void {
    const input: HTMLInputElement = document.getElementById('input') as HTMLInputElement;
    const showMarkersInput: HTMLInputElement = document.getElementById('show-markers') as HTMLInputElement;
    const unescapeInput: HTMLInputElement = document.getElementById('unescape') as HTMLInputElement;
    const precision5Input: HTMLInputElement = document.getElementById('precision5') as HTMLInputElement;
    this.map = new google.maps.Map(document.getElementById('map'), {
      center: mapCenterDefault,
      zoom: 14,
      clickableIcons: false,
      disableDefaultUI: true,
      fullscreenControl: true,
      mapTypeControlOptions: { mapTypeIds: [] },
      zoomControl: true,
      zoomControlOptions: {
        position: google.maps.ControlPosition.TOP_RIGHT,
      },
      streetViewControl: false,
      gestureHandling: 'greedy',
      styles: [...greyMapThemeStyles],
    });
    const pointsDiv = document.getElementById('points');
    let polyline: google.maps.Polyline;
    let markers: google.maps.Marker[] = [];
    let isShowMarkers = false;
    let isUnescape = false;
    let isPrecision5 = false;

    const updateVisibleMarkersOnResize = () => {
      if (!isShowMarkers) {
        return;
      }
      const bounds = this.map.getBounds();
      markers.forEach((marker) => {
        marker.setVisible(bounds.contains(marker.getPosition()));
      });
    };

    const updatePolyline = () => {
      if (polyline) {
        polyline.setMap(null);
      }
      markers.forEach((marker) => marker.setMap(null));
      markers = [];

      let encoded = input.value;
      if (!encoded) return;
      if (isUnescape) {
        encoded = encoded.replace(/\\\\/g, '\\');
      }

      try {
        const latlngs = decode(encoded, isPrecision5 ? 5 : 6).map((v) => ({ lat: v[0], lng: v[1] }));
        polyline = new google.maps.Polyline({
          path: latlngs,
          strokeColor: 'blue',
          strokeWeight: 4,
        });
        polyline.setMap(this.map);
        this.map.fitBounds(getPolylineBounds(polyline));
        latlngs.forEach((latlng, index) => {
          const isWideLabel = index >= 1000;
          const marker = new google.maps.Marker({
            position: latlng,
            visible: isShowMarkers,
            map: this.map,
            optimized: true,
            label: {
              text: '' + (index + 1),
              fontSize: '10px',
            },
            icon: {
              anchor: new google.maps.Point(0, 0),
              labelOrigin: isWideLabel ? new google.maps.Point(18, 7) : new google.maps.Point(10, 7),
              url:
                window.origin +
                (index < 1000
                  ? '/assets/icons/polyline-viewer/rectangle.png'
                  : '/assets/icons/polyline-viewer/rectangle-wide.png'),
            } satisfies google.maps.Icon,
          });
          marker.set('index', index + 1);
          markers.push(marker);
        });
      } catch (error) {
        console.error('Invalid polyline:', error);
        pointsDiv.innerHTML = 'Invalid polyline';
      }
    };

    const getPolylineBounds = (poly: google.maps.Polyline) => {
      const bounds = new google.maps.LatLngBounds();
      poly.getPath().forEach(function (item) {
        bounds.extend(new google.maps.LatLng(item.lat(), item.lng()));
      });
      return bounds;
    };

    input.addEventListener('input', updatePolyline);
    showMarkersInput.addEventListener('input', (event) => {
      isShowMarkers = event.target['checked'];
      const bounds = this.map.getBounds();
      markers.forEach((marker) => {
        marker.setVisible(isShowMarkers && bounds.contains(marker.getPosition()));
      });
    });
    unescapeInput.addEventListener('input', (event) => {
      isUnescape = event.target['checked'];
      updatePolyline.call(this);
    });
    precision5Input.addEventListener('input', (event) => {
      isPrecision5 = event.target['checked'];
      updatePolyline.call(this);
    });

    google.maps.event.addListener(this.map, 'tilesloaded', updateVisibleMarkersOnResize);
  }
}
